/** @typedef {'matte' | 'felt' | 'cardboard' | 'paper' | 'wood'} BoardMatSurfaceKind */

/** @typedef {{ id: string, name: string, hex: string, surface: BoardMatSurfaceKind }} BoardMatPreset */

/** @type {BoardMatPreset[]} */
export const BOARD_MAT_PALETTE = [
    { id: "classic-slate", name: "Classic Slate", hex: "#3A3A3A", surface: "matte" },
    { id: "cardboard-kraft", name: "Cardboard Kraft", hex: "#C2A687", surface: "cardboard" },
    { id: "cardtable-green", name: "Cardtable Green", hex: "#2E5A44", surface: "felt" },
    { id: "deep-ocean-blue", name: "Deep Ocean Blue", hex: "#1A2A4A", surface: "matte" },
    { id: "nordic-birch", name: "Nordic Birch", hex: "#E0D9C5", surface: "paper" },
    { id: "espresso-walnut", name: "Espresso Walnut", hex: "#3E2723", surface: "wood" },
    { id: "muted-sage", name: "Muted Sage", hex: "#6E7E70", surface: "matte" },
    { id: "dusty-rose", name: "Dusty Rose", hex: "#9E7B74", surface: "matte" },
    { id: "mist-gray", name: "Mist Gray", hex: "#CCCCCC", surface: "paper" },
    { id: "wine-felt", name: "Wine Felt", hex: "#722F37", surface: "felt" },
];

export const DEFAULT_BOARD_MAT_ID = "nordic-birch";

/** Prefers hex (`#E0D9C5`); legacy values may be palette ids. */
export const BOARD_MAT_STORAGE_KEY = "jigsaw.boardMatHex";
/** @deprecated Read for migration only. */
const BOARD_MAT_STORAGE_KEY_LEGACY = "jigsaw.boardMatId";

/** @type {Record<string, string>} */
const LEGACY_MAT_IDS = {
    "studio-charcoal": "classic-slate",
    "studio-obsidian": "classic-slate",
    "midnight-blue": "deep-ocean-blue",
};

/** @returns {BoardMatPreset} */
export function getBoardMatById(id) {
    const resolved = LEGACY_MAT_IDS[id] ?? id;
    return BOARD_MAT_PALETTE.find((m) => m.id === resolved) ?? BOARD_MAT_PALETTE[0];
}

function hexToRgb(hex) {
    const h = hex.replace("#", "");
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function boardMatRelativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    const channel = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    const [rs, gs, bs] = [r, g, b].map(channel);
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** True for light mats where groove highlights must be toned down. */
export function isLightBoardMat(hex) {
    return boardMatRelativeLuminance(hex) > 0.42;
}

/**
 * Engraved groove stroke + filter opacities tuned to mat brightness.
 * Light mats: softer highlight and shadow for a lighter engraved look on textured surfaces.
 *
 * @returns {{ stroke: string, shadowOpacity: number, highlightOpacity: number }}
 */
export function getGrooveStyleForMat(hex) {
    const lum = boardMatRelativeLuminance(hex);
    const t = Math.min(1, Math.max(0, (lum - 0.1) / 0.6));

    const shadowOpacity = 0.4 + t * 0.17;
    const highlightOpacity = 0.22 - t * 0.15;
    const strokeAlpha = 0.42 + t * 0.18;
    const strokeGray = Math.round(22 - t * 10);

    return {
        stroke: `rgba(${strokeGray}, ${strokeGray}, ${strokeGray}, ${strokeAlpha.toFixed(2)})`,
        shadowOpacity: Number(shadowOpacity.toFixed(2)),
        highlightOpacity: Number(Math.max(0.05, highlightOpacity).toFixed(2)),
    };
}

export function getBoardMatByHex(hex) {
    if (!hex || typeof hex !== "string") return null;
    const normalized = hex.trim().toLowerCase();
    return BOARD_MAT_PALETTE.find((m) => m.hex.toLowerCase() === normalized) ?? null;
}

export function loadBoardMatId() {
    try {
        const saved =
            localStorage.getItem(BOARD_MAT_STORAGE_KEY) ||
            localStorage.getItem(BOARD_MAT_STORAGE_KEY_LEGACY);
        if (saved) {
            const byHex = getBoardMatByHex(saved);
            if (byHex) return byHex.id;
            const resolved = LEGACY_MAT_IDS[saved] ?? saved;
            if (BOARD_MAT_PALETTE.some((m) => m.id === resolved)) return resolved;
        }
    } catch {
        /* ignore */
    }
    return DEFAULT_BOARD_MAT_ID;
}

/** Persist the mat as hex so palette renames do not break the preference. */
export function saveBoardMatHex(hex) {
    try {
        const mat = getBoardMatByHex(hex) ?? getBoardMatById(DEFAULT_BOARD_MAT_ID);
        localStorage.setItem(BOARD_MAT_STORAGE_KEY, mat.hex);
        localStorage.removeItem(BOARD_MAT_STORAGE_KEY_LEGACY);
    } catch {
        /* ignore */
    }
}
